import { useCallback } from 'react';
import { useTranslateTextsMutation } from '../redux/services/translations';
import {
  extractSequentialChunks,
  mergeChunks,
} from '../utils/helpers/translateHelper.jsx';

/**
 * Custom hook for translation of plain text and HTML content.
 *
 * Supports:
 * - Plain text translation
 * - HTML-aware translation preserving structure and protected attributes
 * - Batch translation of multiple values
 *
 * Returns translated content in the same structure as input.
 */
export function useAutoTranslations() {
  /**
   * RTK Query mutation for translation API communication.
   */
  const [translateTexts, { isLoading, isError }] = useTranslateTextsMutation();

  /**
   * Processes input values and translates them.
   *
   * @param {string|string[]} values - Single value or array of values to translate
   * @param {string} targetLang - Target language code
   * @param {string} sourceLang - Source language code
   * @param {string} provider - Translation provider identifier
   * @returns string|
   */
  const processContent = useCallback(
    async (
      values,
      targetLang = 'EN',
      sourceLang = 'SK',
      provider = 'deepl',
    ) => {
      if (values === null || values === undefined) return null;

      const isArray = Array.isArray(values);
      const inputs = isArray ? values : [values];


      try {
        /**
         * Preprocess all inputs:
         * - Detect HTML content
         * - Extract translatable chunks from HTML
         * - Preserve plain text as-is
         *
         */
        const prepared = inputs.map((value) => {
          const isHtml = /<\/?[a-z][\s\S]*>/i.test(value);
          //if plaintext, not need for processing
          if (!isHtml) {
            return {
              type: 'text',
              value,
            };
          }
          //else protect html
          const extracted = extractSequentialChunks(value);
          const textsToTranslate = extracted.translatable.map(
            (item) => item.html,
          );

          return {
            type: 'html',
            original: value,
            extracted,
            textsToTranslate,
          };
        });
        /**
         * building one batch from translatable texts
         */
        const allTexts = prepared.flatMap((item) => {
          if (item.type === 'text') return [item.value];
          return item.textsToTranslate;
        });
        /**
         * If no translatable content exists,
         * return null
         */
        if (allTexts.length === 0) {
          return null;
        }
        /**
         * Send batch translation request to backend.
         */
        const data = await translateTexts({
          texts: allTexts,
          targetLang,
          sourceLang,
          provider,
        }).unwrap();

        const translations = data?.translations || [];

        let cursor = 0;


        /**
         * Rebuild translated outputs while preserving
         * original HTML structure and protected elements.
         */
        const results = prepared.map((item) => {
          if (item.type === 'text') {
            const translated = translations[cursor];
            cursor++;
            return translated ?? item.value;
          }

          const chunkCount = item.textsToTranslate.length;

          const translatedChunks = item.extracted.translatable.map((chunk, i) => {
            const translated = translations[cursor + i];
            return {
              ...chunk,
              html: translated ?? chunk.html,
            };
          });

          cursor += chunkCount;

          return mergeChunks(
            item.extracted.skeleton,
            translatedChunks,
            item.extracted.nonTranslatable,
            item.extracted.protectedAttrs,
          );
        });

        return isArray ? results : results[0];
      } catch (error) {
        return null;
      }
    },
    [translateTexts],
  );

  return { processContent, isLoading, isError };
}
